import { Grid, Slide } from '@mui/material';
import { HSpacer, RoutalPalette, VSpacer } from '@nx-smartmonkey/ui';

import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import CrossIcon from '../../assets/svg/CrossIcon';
import { STOP_STATUS } from '../../constants/Stops';
import { FlatRoute } from '../../domain/route/FlatRoute';
import { Route } from '../../domain/route/Route';
import { RouteSelector } from '../../pages/app/components/RouteSelector';
import { FREE_STOP_LIMIT } from '../../pages/app/components/upgradeSubscription/UpgradeSubscriptionComponent';
import { usePermissions } from '../../pages/app/hooks/usePermissions';
import { useUpgradeDialogVisibility } from '../../pages/app/hooks/useUpgradeDialogVisibility';
import { useCopyStops } from '../../pages/app/route/hooks/useCopyStops';
import { useDeleteStops } from '../../pages/app/route/hooks/useDeleteStops';

import { useRetrieveTemporalRoute } from '../../pages/app/route/hooks/useRetrieveTemporalRoute';
import { Text } from '../../react-components/Text';
import { StyledFooterButton } from '../StyledFooterButton';
import { SelectorStopsManagementMenu } from './SelectorStopsManagementMenu';

interface StopsManagementMenuProps {
  isOpen: boolean;
  onClose: () => void;
  selectedRoute?: Route;
  mode: `copy` | `delete`;
  portalElementId?: string;
}

export const StopsManagementMenu = ({
  isOpen,
  onClose,
  selectedRoute,
  mode,
  portalElementId,
}: StopsManagementMenuProps) => {
  const { isSoloDriver, hasSubscription } = usePermissions();

  const [selectedStopsIds, setSelectedStopsIds] = useState<string[]>([]);
  const [selectedRouteFromSelector, setSelectedRouteFromSelector] = useState<FlatRoute | undefined>(undefined);
  const [selectedRouteShowed, setSelectedRouteShowed] = useState<Route | undefined>(selectedRoute);

  const {
    run: deleteStops,
    isLoading: deletingStops,
    success: deletingSuccess,
  } = useDeleteStops({ route: selectedRoute! });
  const { run: copyStops, isLoading: copyingStops, success: copyingSuccess } = useCopyStops({ route: selectedRoute! });
  const { data: temporalRoute, run: retrieveTemporal, isLoading: retrievingTemporal } = useRetrieveTemporalRoute();
  const { setDialogVisibility } = useUpgradeDialogVisibility();
  const totalStops = (selectedRoute?.stops.length ?? 0) + selectedStopsIds.length;

  const isLoadingThings = deletingStops || copyingStops || retrievingTemporal;
  const isSuccessAction = deletingSuccess || copyingSuccess;

  useEffect(() => {
    if (isSuccessAction) {
      onClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccessAction]);

  useEffect(() => {
    if (selectedRouteFromSelector) {
      setSelectedStopsIds([]);
      retrieveTemporal(selectedRouteFromSelector.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRouteFromSelector]);

  useEffect(() => {
    if (mode !== `delete`) setSelectedRouteShowed(temporalRoute);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [temporalRoute]);

  const pendingStops = selectedRouteShowed?.stops.filter((stop) => stop.status === STOP_STATUS.pending);
  // const completedStops = selectedRouteShowed?.stops.filter((stop) => stop.status === STOP_STATUS.completed);
  // const canceledStops = selectedRouteShowed?.stops.filter((stop) => stop.status === STOP_STATUS.canceled);
  // const incompleteStops = selectedRouteShowed?.stops.filter((stop) => stop.status === STOP_STATUS.incomplete);

  if (!selectedRoute) return null;

  const footerButton = () => {
    return (
      <StyledFooterButton
        disabled={isLoadingThings || (mode !== `delete` && !selectedRouteFromSelector) || selectedStopsIds.length === 0}
        onClick={() => {
          if (mode !== `delete` && totalStops > FREE_STOP_LIMIT && !hasSubscription && isSoloDriver) {
            setDialogVisibility(true);
          } else {
            if (mode === `copy`) {
              if (selectedRouteShowed) {
                copyStops({
                  route_destination_id: selectedRoute.id,
                  route_origin_id: selectedRouteShowed.id,
                  stops: selectedStopsIds,
                });
              }
            } else {
              deleteStops({ stops: selectedStopsIds });
            }
          }
        }}
        style={{
          width: `100%`,
          backgroundColor: mode !== `delete` ? RoutalPalette.primary40 : RoutalPalette.error.medium,
          border: `none`,
        }}
      >
        <Text
          style={{ cursor: `pointer`, textAlign: `justify`, color: RoutalPalette.white }}
          intlId={mode === `copy` ? `driver.stops.management.button.copy` : `driver.stops.management.button.delete`}
        />
        {mode === `delete` && (
          <>
            <HSpacer small />
            <Text style={{ cursor: `pointer`, textAlign: `justify`, color: RoutalPalette.white }}>
              {`(`}
              {selectedStopsIds.length ?? 0}
              {`)`}
            </Text>
          </>
        )}
      </StyledFooterButton>
    );
  };

  const content = (
    <Slide
      key={`sliding-menu-stops-management`}
      in={isOpen}
      direction={`up`}
      mountOnEnter
      unmountOnExit
      timeout={{ enter: 400, exit: 300 }}
      style={{ zIndex: 1006, top: 0, left: 0, position: `absolute` }}
    >
      <Grid
        style={{
          height: `100%`,
          width: `100%`,
          overflowY: `hidden`,
        }}
      >
        <Grid
          container
          height={`calc(100% - 92px)`} // - footer height
          style={{
            backgroundColor: RoutalPalette.white,
            flexWrap: `nowrap`,
            flexDirection: `column`,
          }}
        >
          <Grid
            container
            direction="row"
            wrap="nowrap"
            height="64px"
            alignItems="center"
            justifyContent={`space-between`}
            style={{
              padding: `20px`,
              borderBottom: `1px solid ${RoutalPalette.neutral10}`,
            }}
          >
            <Text variant="h6" isBold intlId={`driver.route.stops.config.${mode}`} />
            <HSpacer small />
            <CrossIcon
              onClick={onClose}
              style={{ color: `${RoutalPalette.secondary60}`, height: `22px`, width: `22px` }}
            />
          </Grid>
          <Grid
            style={{
              overflowY: `auto`,
            }}
          >
            {mode !== `delete` && (
              <Grid
                item
                container
                direction="row"
                wrap="nowrap"
                alignItems="flex-start"
                flexDirection={`column`}
                style={{
                  padding: `12px 20px`,
                  marginBottom: `12px`,
                }}
              >
                <Text
                  variant="body"
                  intlId="driver.stops.management.from"
                  style={{ color: RoutalPalette.secondary00 }}
                />

                <VSpacer small />
                <RouteSelector
                  destinationRouteId={selectedRoute.id}
                  disabled={isLoadingThings}
                  selectedRoute={selectedRouteFromSelector}
                  onChange={(flatRoute) => {
                    if (flatRoute) setSelectedRouteFromSelector(flatRoute);
                  }}
                />
              </Grid>
            )}

            <SelectorStopsManagementMenu
              type="pending"
              stops={pendingStops}
              selectedStopsIds={selectedStopsIds}
              setSelectedStopsIds={setSelectedStopsIds}
              disabled={isLoadingThings}
            />
          </Grid>

          <Grid
            item
            container
            style={{
              bottom: 0,
              left: 0,
              width: `100%`,
              height: `92px`,
              zIndex: 1001,
              backgroundColor: RoutalPalette.white,
              padding: `20px`,

              display: `flex`,
              flexWrap: `wrap`,
              fontSize: `16px`,
              position: `absolute`,
              alignContent: `center`,
              boxShadow: `0px 0px 8px 4px rgba(125, 154, 150, 0.15)`,
              borderTop: `1px solid ${RoutalPalette.neutral10}`,
            }}
          >
            {footerButton()}
          </Grid>
        </Grid>
      </Grid>
    </Slide>
  );
  const element = document.getElementById(portalElementId ?? ``);
  if (element) {
    return createPortal(content, element as HTMLElement);
  }
  return content;
};
